import React, { useMemo, useState } from 'react';
import type { OpsMissionProposal, OpsMission, OpsMissionStep, OpsAgentEvent, OpsLane } from '../convex-types';
import PixelArtOffice from './PixelArtOffice';
import StageHeader from './StageHeader';
import StageFilters from './StageFilters';
import SignalFeed from './SignalFeed';

import './OpsPanel.css';

interface OpsPanelProps {
  approvals?: any[];
  proposals?: OpsMissionProposal[];
  missions?: OpsMission[];
  steps?: OpsMissionStep[];
  events?: OpsAgentEvent[];
  onApprove: (proposalId: string) => void;
  onReject: (proposalId: string) => void;
  onApproveApproval: (approvalId: string) => void;
  onRejectApproval: (approvalId: string) => void;
}

const laneLabel: Record<OpsLane, string> = {
  content_ops: 'Content Ops',
  growth: 'Growth',
  product_systems: 'Product Systems',
  platform_execution: 'Platform Execution',
};

const statusBadge = (status: string) => `status-badge status-${status}`;

const OpsPanel: React.FC<OpsPanelProps> = ({ approvals = [], proposals = [], missions = [], steps = [], events = [], onApprove, onReject, onApproveApproval, onRejectApproval }) => {
  const sortedSteps = useMemo(() => {
    return [...steps].sort((a, b) => b._creationTime - a._creationTime);
  }, [steps]);

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => b._creationTime - a._creationTime).slice(0, 30);
  }, [events]);

  // Map events to terminal messages for PixelArtOffice
  const terminalMessages = useMemo(() => {
    return events.slice(0, 4).map((e, i) => ({
      id: e._id,
      agent: e.agentId.slice(0, 8) || 'System',
      message: e.title,
      timestamp: e._creationTime,
    }));
  }, [events]);

  // Determine agent states from events
  const agentStates = useMemo(() => {
    const hasRunning = missions.some(m => m.status === 'running');
    const hasPending = proposals.some(p => p.status === 'pending');
    
    return [
      { id: '1', role: 'coordinator' as const, name: 'Peter', status: hasPending ? 'active' : 'idle' },
      { id: '2', role: 'executor' as const, name: 'James', status: hasRunning ? 'busy' : 'idle' },
      { id: '3', role: 'observer' as const, name: 'John', status: 'active' },
    ];
  }, [missions, proposals]);

  const [filter, setFilter] = useState<'All'|'Approvals'|'Proposals'|'Missions'|'Steps'>('All');
  const filteredEvents = useMemo(() => {
    if (filter === 'All') return sortedEvents;
    if (filter === 'Approvals') return sortedEvents.filter(e => e.title?.toLowerCase().includes('approval'));
    if (filter === 'Proposals') return sortedEvents.filter(e => e.title?.toLowerCase().includes('proposal'));
    if (filter === 'Missions') return sortedEvents.filter(e => e.title?.toLowerCase().includes('mission'));
    if (filter === 'Steps') return sortedEvents.filter(e => e.title?.toLowerCase().includes('step'));
    return sortedEvents;
  }, [filter, sortedEvents]);

  return (
    <div className="ops-panel">
      <StageHeader title="Ops Loop" subtitle="Stage 1 — Live Operations" stats={[
        { label: 'Approvals', value: String(approvals.length) },
        { label: 'Proposals', value: String(proposals.length) },
        { label: 'Missions', value: String(missions.length) },
        { label: 'Steps', value: String(steps.length) },
      ]} />
      <StageFilters value={filter} onChange={setFilter} options={["All","Approvals","Proposals","Missions","Steps"]} />
      <SignalFeed items={filteredEvents.slice(0,6).map(e=>({id:e._id,text:e.title,ts:new Date(e._creationTime).toLocaleTimeString()}))} />
      {/* Pixel Art Office - Agent Visualization */}
      <PixelArtOffice 
        agents={agentStates}
        terminalMessages={terminalMessages}
        showConnections={true}
      />

      <div className="ops-grid">
        <section className="ops-card">
          <h3>Approvals (Gatekeeper)</h3>
          {approvals.length === 0 ? (
            <p className="ops-empty">No pending approvals.</p>
          ) : (
            <div className="ops-list">
              {approvals.map((a) => (
                <div key={a._id} className="ops-item">
                  <div className="ops-item-header">
                    <div>
                      <div className="ops-title">{a.title}</div>
                      <div className="ops-meta">Type: {a.approvalType}</div>
                      {a.description && <div className="ops-meta">{a.description}</div>}
                    </div>
                    <span className={statusBadge(a.status)}>{a.status}</span>
                  </div>
                  <div className="ops-actions">
                    <button className="btn-approve" onClick={() => onApproveApproval(a._id)}>Approve</button>
                    <button className="btn-reject" onClick={() => onRejectApproval(a._id)}>Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="ops-card">
          <h3>Proposals (Strict Approval)</h3>
          {proposals.length === 0 ? (
            <p className="ops-empty">No pending proposals.</p>
          ) : (
            <div className="ops-list">
              {proposals.map((p) => (
                <div key={p._id} className="ops-item">
                  <div className="ops-item-header">
                    <div>
                      <div className="ops-title">{p.title}</div>
                      <div className="ops-meta">Lane: {laneLabel[p.lane]}</div>
                    </div>
                    <span className={statusBadge(p.status)}>{p.status}</span>
                  </div>
                  <div className="ops-actions">
                    <button className="btn-approve" onClick={() => onApprove(p._id)}>Approve</button>
                    <button className="btn-reject" onClick={() => onReject(p._id)}>Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="ops-card">
          <h3>Missions</h3>
          {missions.length === 0 ? (
            <p className="ops-empty">No missions yet.</p>
          ) : (
            <div className="ops-list">
              {missions.map((m) => (
                <div key={m._id} className="ops-item">
                  <div className="ops-item-header">
                    <div className="ops-title">{m.title}</div>
                    <span className={statusBadge(m.status)}>{m.status}</span>
                  </div>
                  {m.proposalId && <div className="ops-meta">Proposal: {m.proposalId}</div>}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="ops-card">
          <h3>Steps Queue</h3>
          {sortedSteps.length === 0 ? (
            <p className="ops-empty">No steps queued.</p>
          ) : (
            <div className="ops-list">
              {sortedSteps.map((s) => (
                <div key={s._id} className="ops-item">
                  <div className="ops-item-header">
                    <div>
                      <div className="ops-title">{s.kind}</div>
                      <div className="ops-meta">Mission: {s.missionId}</div>
                    </div>
                    <span className={statusBadge(s.status)}>{s.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="ops-card">
          <h3>Event Stream</h3>
          {sortedEvents.length === 0 ? (
            <p className="ops-empty">No events yet.</p>
          ) : (
            <div className="ops-list">
              {sortedEvents.map((e) => (
                <div key={e._id} className="ops-item">
                  <div className="ops-item-header">
                    <div>
                      <div className="ops-title">{e.title}</div>
                      <div className="ops-meta">{e.summary}</div>
                    </div>
                    <span className={statusBadge(e.kind)}>{e.kind}</span>
                  </div>
                  <div className="ops-tags">
                    {e.tags?.map((t) => (
                      <span key={t} className="ops-tag">{t}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default OpsPanel;
